/**
 * Campos del formulario de trabajador (alta y edición).
 *
 * Compartido entre WorkersAdmin (alta), el wizard de invitación (A5) y la
 * edición de trabajadores (Fase B). El único parámetro es `mode`: cambia el
 * correo (identidad, no editable en edición) y las etiquetas de contraseña.
 */
import { AdminUserCreate } from '../../services/apiClient';

type Props = {
  draft: AdminUserCreate;
  set: <K extends keyof AdminUserCreate>(k: K, v: AdminUserCreate[K]) => void;
  mode: 'create' | 'edit';
  /** Oculta el campo de contraseña (p.ej. wizard de invitación: siempre es alta por email). */
  hidePassword?: boolean;
};

export function WorkerFormFields({ draft, set, mode, hidePassword = false }: Props) {
  // En edición el correo es de sólo lectura y viene del trabajador, así que
  // vacío significa "no tiene"; en el alta, lo que el responsable lleve
  // escrito hasta ahora.
  const sinCorreo = !draft.email;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <label className="block">
          <span className="label">Nombre</span>
          <input className="field" value={draft.first_name} onChange={(e) => set('first_name', e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Apellidos</span>
          <input className="field" value={draft.last_name} onChange={(e) => set('last_name', e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Correo{mode === 'create' ? ' (opcional)' : ''}</span>
          <input
            className={mode === 'edit' ? 'field opacity-60' : 'field'}
            type="email"
            readOnly={mode === 'edit'}
            value={draft.email ?? ''}
            onChange={(e) => set('email', e.target.value)}
          />
          {mode === 'create' && (
            <span className="block text-[12.5px] text-ink-3 mt-1">
              Sin correo, el trabajador entra escaneando un QR que le darás al acabar.
            </span>
          )}
        </label>
        {/* Sin correo no hay ningún sitio donde teclear una contraseña: no
            hay nombre de usuario que la acompañe. En el alta, rellenar las dos
            cosas dejaba a la persona sin correo, sin PIN y sin QR —muerta en
            el sistema—; en la edición, machacaba el hash centinela de quien
            entra por PIN. El backend rechaza las dos (422 y 409): aquí ni
            siquiera se ofrece. */}
        {!hidePassword && !sinCorreo && (
          <label className="block">
            <span className="label">
              {mode === 'edit' ? 'Nueva contraseña (vacío = no cambiar)' : 'Contraseña inicial (mín. 8)'}
            </span>
            <input
              className="field"
              type="text"
              autoComplete="off"
              placeholder={mode === 'edit' ? 'Dejar vacío para no cambiarla' : 'Vacía = enviar invitación por email'}
              value={draft.password ?? ''}
              onChange={(e) => set('password', e.target.value)}
            />
          </label>
        )}
        {!hidePassword && sinCorreo && (
          <div className="block">
            <span className="label">Credencial</span>
            <p className="text-[13px] text-ink-2 leading-relaxed">
              {mode === 'edit'
                ? 'Entra con su PIN, no con contraseña. Si lo ha perdido, cámbiaselo desde la pantalla de quiosco.'
                : 'Sin correo no hay contraseña: al escanear el QR se le asignará un PIN, y ése es su acceso.'}
            </p>
          </div>
        )}
        <label className="block">
          <span className="label">Rol</span>
          <select className="field" value={draft.role} onChange={(e) => set('role', e.target.value as AdminUserCreate['role'])}>
            <option value="EMPLOYEE">Trabajador</option>
            <option value="MANAGER">Responsable</option>
            <option value="ADMIN">Administrador</option>
          </select>
        </label>
        <label className="block">
          <span className="label">DNI / NIE</span>
          <input
            className="field"
            placeholder="12345678Z"
            value={draft.dni ?? ''}
            onChange={(e) => set('dni', e.target.value)}
          />
          <span className="block text-[12.5px] text-ink-3 mt-1">
            Se imprime en el informe para Inspección: es lo que distingue a dos
            personas con el mismo nombre.
          </span>
        </label>
        <label className="block">
          <span className="label">Código de empleado</span>
          <input className="field" placeholder="E-014" value={draft.employee_code ?? ''} onChange={(e) => set('employee_code', e.target.value)} />
        </label>
        {/* El `required`/`max` de este input son inertes: ningún consumidor
            envuelve los campos en un <form> (se guarda con botón onClick),
            así que la obligatoriedad real vive en WorkersAdmin/WelcomeWizard
            (guarda de cliente) y en la API. En edición, vacía = no tocar. */}
        <label className="block">
          <span className="label">Fecha de alta{mode === 'create' ? ' *' : ''}</span>
          <input
            className="field"
            type="date"
            required
            max={new Date().toISOString().slice(0, 10)}
            value={draft.hire_date ?? ''}
            onChange={(e) => set('hire_date', e.target.value)}
          />
          <span className="block text-[12.5px] text-ink-3 mt-1">
            {mode === 'create'
              ? 'De ella sale el prorrateo de vacaciones: sin fecha, quien entra en noviembre devenga el año entero.'
              : 'Vacía = no cambiar. De ella sale el prorrateo de vacaciones.'}
          </span>
        </label>
        <label className="block">
          <span className="label">Horas semanales</span>
          <input
            className="field"
            type="number"
            min={1}
            max={60}
            value={draft.weekly_hours}
            onChange={(e) => {
              const h = Number(e.target.value);
              // Menos de 40 h suele ser parcial; se marca solo pero se puede destachar.
              set('weekly_hours', h);
              set('is_part_time', h < 40);
            }}
          />
        </label>
      </div>
      <label className="flex items-center gap-2.5 text-[14px] text-ink-2">
        <input type="checkbox" checked={draft.is_part_time} onChange={(e) => set('is_part_time', e.target.checked)} />
        Tiempo parcial — sin horas extra (art. 12.4.c ET); complementarias solo con pacto
      </label>
    </>
  );
}
